/**
 * Pure mappings between the panel's drafts and the `/jobs` wire (plan 13/13b/13g/13h). Kept out of the component so
 * the persona + wire rules are reviewable in one place: job attributes come from {@link RoleDraft} (both
 * personas), workflow settings from {@link WorkflowDraft} (recruiter card; customers get derived/echoed values).
 * Sourcing strategy is always `Agent` on create — deliberately not user-selectable (plan 13).
 *
 * The update mapper exists because the backend's Update is NOT uniformly omit-=-preserve (plan 13b):
 * `InitialReachOutMessageTemplate`/`OwnerId` are direct assignments (null WIPES them) and
 * `IsMessagingCreative ?? true` (null FLIPS a false to true) — so fields the panel doesn't manage are echoed
 * back from the loaded job. `coordinationProcessSettings` has presence-tracking on the wire
 * (`HasCoordinationProcessSettings`): the key must be ABSENT (undefined) to preserve — an explicit null clears.
 */

import type { components } from '@/types/apiSchema';

import { emphasisForRequest, savedEmphasis } from './emphasis.ts';
import {
  countriesForRequest,
  hasSomewhereToSearch,
  targetCityForRequest,
} from './location.ts';
import {
  isManagementLevel,
  type RoleDraft,
  type WorkflowDraft,
} from './types.ts';

type JobCreateRequestBody = components['schemas']['JobCreateRequest'];
type JobUpdateRequestBody = components['schemas']['JobUpdateRequest'];
export type JobResponseBody = components['schemas']['JobResponse'];

/** `save` = park the draft / save changes; `publish` = create as `New` (create) or PUT + kick sourcing (edit). */
export type SubmitIntent = 'save' | 'publish';

/**
 * The publish-blocking checks, as human field labels (used verbatim in the "Missing: …" toast). Saves only
 * require a title (+ a resolved company) — the point of a draft is to park an incomplete brief. The coordination
 * pair is checked for BOTH intents: the backend validates coordination settings on every create/update, so an
 * incomplete pair with tracking on would 400 a save too.
 */
export const missingForSubmit = (
  role: RoleDraft,
  workflow: WorkflowDraft,
  intent: SubmitIntent,
): string[] => {
  const missing: string[] = [];
  if (!role.title.trim()) missing.push('title');
  if (!workflow.companyId) missing.push('company');
  if (
    workflow.isTrackingCoordinationEvents &&
    (!workflow.coordinationRecruiterId || !workflow.coordinationHiringManagerId)
  ) {
    missing.push('coordination recruiter + hiring manager');
  }
  if (intent === 'save') return missing;

  if (!role.description.trim()) missing.push('description');
  if (role.mustHave.length === 0) missing.push('a must-have skill');
  if (!role.location.trim()) missing.push('location');
  if (role.locationTypes.length === 0) missing.push('work arrangement');
  if (!hasSomewhereToSearch(role)) missing.push('countries');
  if (role.contractTypes.length === 0) missing.push('employment type');
  return missing;
};

export const toJobCreateRequest = (
  role: RoleDraft,
  workflow: WorkflowDraft,
  intent: SubmitIntent,
): JobCreateRequestBody => {
  const skillEmphasis = emphasisForRequest(role);
  return {
    slackChannelId: workflow.slackChannelId ?? null,
    title: role.title.trim(),
    description: role.description,
    location: role.location.trim(),
    companyId: workflow.companyId,
    ownerId: workflow.ownerId || null,
    userIds: workflow.userIds.length > 0 ? workflow.userIds : null,
    mustHaveSkills: role.mustHave,
    niceToHaveSkills: role.niceToHave,
    // only when a skill has one, as the chat intake sends it
    ...(Object.keys(skillEmphasis).length > 0 && { skillEmphasis }),
    sourcingStrategy: 'Agent',
    sourcingState: intent === 'publish' ? 'New' : 'Draft',
    locationTypes: role.locationTypes,
    contractTypes: role.contractTypes,
    // stale/renamed levels (e.g. a future restored draft) are dropped rather than rejected with a 400
    seniorityLevels: role.seniority.filter(isManagementLevel),
    targetCity: targetCityForRequest(role.targetCity, role.locationTypes),
    ...countriesForRequest(role.countries, role.isAnywhere, role.locationTypes),
    // only when there's a preference, as the chat intake sends it; left out is Any
    ...(role.employerSize &&
      role.employerSize !== 'Any' && { employerSize: role.employerSize }),
    // likewise only when there are some, in the order picked; left out is any industry
    ...(role.employerIndustries?.length && {
      employerIndustries: role.employerIndustries,
    }),
    // and extra search titles only when there are some, as the chat intake sends them
    ...(role.extraSearchTitles.length > 0 && {
      extraSearchTitles: role.extraSearchTitles,
    }),
    companies: role.companies.trim() || null,
    screeningQuestions: role.screeningQuestions,
    exemplarProfileUrls: role.exemplarUrl.trim()
      ? [role.exemplarUrl.trim()]
      : [],
    // recruiter-tuned toggles; `undefined` is omitted from the JSON so the backend's own defaults apply
    isMessagingAutomated: workflow.isMessagingAutomated,
    isReachOutAutomated: workflow.isReachOutAutomated,
    isShortlistingAutomated: workflow.isShortlistingAutomated,
    isInvitationFollowUpEnabled: workflow.isInvitationFollowUpEnabled,
    isPrescreeningEnabled: workflow.isPrescreeningEnabled,
    isPublicCandidateListEnabled: workflow.isPublicCandidateListEnabled,
    isTrackingCoordinationEvents: workflow.isTrackingCoordinationEvents,
    isCoordinationActionAutoApproved: workflow.isCoordinationActionAutoApproved,
    coordinationProcessSettings:
      workflow.isTrackingCoordinationEvents &&
      workflow.coordinationRecruiterId &&
      workflow.coordinationHiringManagerId
        ? {
            recruiterId: workflow.coordinationRecruiterId,
            hiringManagerId: workflow.coordinationHiringManagerId,
          }
        : null,
  };
};

/**
 * Drafts + the LOADED job → the PUT body. Everything the panel manages comes from the drafts; everything it
 * doesn't is echoed from `job` so the not-uniform Update semantics can't wipe or flip it (see module docstring).
 */
export const toJobUpdateRequest = (
  role: RoleDraft,
  workflow: WorkflowDraft,
  job: JobResponseBody,
): JobUpdateRequestBody => ({
  slackChannelId: workflow.slackChannelId ?? null,
  title: role.title.trim(),
  description: role.description,
  // Keep the required location on an incomplete draft save; published edits validate it before reaching here.
  location: role.location.trim() || job.location,
  ownerId: workflow.ownerId || job.ownerId || null,
  // null = keep existing team; the backend only replaces userIds when NON-EMPTY, so `[]` can't clear either —
  // team-clearing is impossible on update for every client (pre-existing backend behavior, JobForm included)
  userIds: workflow.userIds.length > 0 ? workflow.userIds : null,
  mustHaveSkills: role.mustHave,
  niceToHaveSkills: role.niceToHave,
  // Always sent: the backend keeps what it has for null, so a level set back to normal would come back.
  skillEmphasis: emphasisForRequest(role),
  locationTypes: role.locationTypes,
  contractTypes: role.contractTypes,
  seniorityLevels: role.seniority.filter(isManagementLevel),
  // Sent empty when there's none, never null: the backend keeps what it has for null, so an office city removed, or
  // one a remote job no longer has, and emptied example companies would come back on reload.
  targetCity: targetCityForRequest(role.targetCity, role.locationTypes) ?? '',
  // Always both, never null: the backend keeps what it has for null, so removed countries would come back, and a
  // job moved off remote-only work must drop Anywhere in the same request.
  ...countriesForRequest(role.countries, role.isAnywhere, role.locationTypes),
  // Always sent: the backend keeps what it has for null, so a band set back to Any would come back.
  employerSize: role.employerSize ?? job.employerSize,
  // Always sent too: industries cleared to any would come back otherwise.
  employerIndustries: role.employerIndustries ?? job.employerIndustries,
  // Always sent too: titles removed would come back otherwise.
  extraSearchTitles: role.extraSearchTitles,
  companies: role.companies.trim(),
  screeningQuestions: role.screeningQuestions,
  // the panel edits only the first exemplar URL — keep any API-created tail instead of truncating it
  exemplarProfileUrls: role.exemplarUrl.trim()
    ? [role.exemplarUrl.trim(), ...job.exemplarProfileUrls.slice(1)]
    : job.exemplarProfileUrls.slice(1),
  // workflow toggles are seeded from the job on load, so these are explicit echoes-or-edits, never null
  isMessagingAutomated: workflow.isMessagingAutomated,
  isReachOutAutomated: workflow.isReachOutAutomated,
  isShortlistingAutomated: workflow.isShortlistingAutomated,
  isInvitationFollowUpEnabled: workflow.isInvitationFollowUpEnabled,
  isPrescreeningEnabled: workflow.isPrescreeningEnabled,
  // The method wins over the legacy flag, which is true only for AI screening: the flag alone would turn a job whose
  // owner screens candidates through their booking link into one with no screening. A changed flag sends no method,
  // so the backend reads the flag.
  screeningMethod:
    workflow.isPrescreeningEnabled === job.isPrescreeningEnabled
      ? job.screeningMethod
      : undefined,
  // Lifecycle fields (plan 13h): recruiters edit these in the card; the workflow is ALWAYS seeded from the job
  // on edit (`jobToWorkflowDraft` — both edit pages), so for customers (no card) they're faithful echoes. The
  // `?? job.x` fallbacks are belts for the optional fields. Clearing the template deliberately sends null — the
  // backend's direct assignment makes that a real clear (the one lifecycle field where empty means "remove").
  isMessagingCreative: workflow.isMessagingCreative ?? job.isMessagingCreative,
  // trim only as the emptiness TEST — send the raw text so an untouched seeded template round-trips
  // byte-identically (trimming would silently rewrite stored whitespace on every unrelated save)
  initialReachOutMessageTemplate: workflow.initialReachOutMessageTemplate.trim()
    ? workflow.initialReachOutMessageTemplate
    : null,
  state: workflow.state ?? job.state,
  // The ONLY legal strategy change is legacy→Agent, so send exactly that and nothing else. This also closes a
  // race: if another session migrated the job and our query refetched, a stale legacy seed can never be sent
  // back (Agent→legacy would 400). An unchanged echo would be harmless (the backend early-returns on equality),
  // but omission keeps the request minimal and the intent explicit.
  sourcingStrategy:
    workflow.sourcingStrategy === 'Agent' && job.sourcingStrategy !== 'Agent'
      ? 'Agent'
      : undefined,
  isTrackingCoordinationEvents: workflow.isTrackingCoordinationEvents,
  isCoordinationActionAutoApproved: workflow.isCoordinationActionAutoApproved,
  // presence-tracked on the wire: absent (undefined) = preserve, null would CLEAR — mirror JobForm's transform
  coordinationProcessSettings:
    workflow.isTrackingCoordinationEvents &&
    workflow.coordinationRecruiterId &&
    workflow.coordinationHiringManagerId
      ? {
          recruiterId: workflow.coordinationRecruiterId,
          hiringManagerId: workflow.coordinationHiringManagerId,
        }
      : undefined,
});

/** Loaded job → the panel's role draft; only the first exemplar URL is editable. */
export const jobToRoleDraft = (job: JobResponseBody): RoleDraft => ({
  title: job.title,
  description: job.description,
  mustHave: [...job.mustHaveSkills],
  niceToHave: [...(job.niceToHaveSkills ?? [])],
  seniority: [...job.seniorityLevels],
  location: job.location,
  targetCity: job.targetCity ?? '',
  locationTypes: [...job.locationTypes],
  countries: [...job.countries],
  isAnywhere: job.isAnywhere,
  skillEmphasis: savedEmphasis(job.skillEmphasis),
  employerSize: job.employerSize,
  employerIndustries: [...job.employerIndustries],
  extraSearchTitles: [...job.extraSearchTitles],
  contractTypes: [...job.contractTypes],
  companies: job.companies ?? '',
  screeningQuestions: [...job.screeningQuestions],
  exemplarUrl: job.exemplarProfileUrls[0] ?? '',
});

/** Loaded job → the workflow draft, seeded with the job's ACTUAL values (the card shows truth on edit, and the
 * update mapper then sends explicit echoes-or-edits). `isPublicCandidateListEnabled` stays unset — it's a
 * create-only wire field; on edit the separate PublicShortlistSettings section owns it. */
export const jobToWorkflowDraft = (job: JobResponseBody): WorkflowDraft => ({
  slackChannelId: job.slackChannel?.channelId ?? null,
  slackChannelName: job.slackChannel?.channelName ?? null,
  companyId: job.companyId,
  ownerId: job.ownerId ?? '',
  userIds: (job.users ?? []).map((user) => user.id),
  isMessagingAutomated: job.isMessagingAutomated,
  isReachOutAutomated: job.isReachOutAutomated,
  isShortlistingAutomated: job.isShortlistingAutomated,
  isInvitationFollowUpEnabled: job.isInvitationFollowUpEnabled,
  isPrescreeningEnabled: job.isPrescreeningEnabled,
  isTrackingCoordinationEvents: job.isTrackingCoordinationEvents,
  isCoordinationActionAutoApproved: job.isCoordinationActionAutoApproved,
  coordinationRecruiterId: job.coordinationProcessSettings?.recruiterId ?? '',
  coordinationHiringManagerId:
    job.coordinationProcessSettings?.hiringManagerId ?? '',
  // lifecycle seeds (plan 13h) — recruiter-editable in the card; faithful echoes for customers
  state: job.state ?? undefined,
  initialReachOutMessageTemplate: job.initialReachOutMessageTemplate ?? '',
  isMessagingCreative: job.isMessagingCreative,
  sourcingStrategy: job.sourcingStrategy,
});
